import axios from 'axios';
const sleep = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isRetryableMethod = (method) => {
    const normalized = (method ?? 'get').toLowerCase();
    return ['get', 'head', 'options', 'put', 'delete'].includes(normalized);
};
const isRetryableError = (error, retryOnStatuses) => {
    const status = error?.response?.status;
    const code = String(error?.code ?? '');
    const isNetworkError = !error?.response;
    const isTimeout = code === 'ECONNABORTED';
    return isNetworkError || isTimeout || (typeof status === 'number' && retryOnStatuses.has(status));
};
const shouldCountAsCircuitFailure = (error) => {
    const status = error?.response?.status;
    if (!error?.response)
        return true;
    return status === 429 || (typeof status === 'number' && status >= 500);
};
const createCircuitOpenError = (baseURL) => {
    const error = new Error(`Circuit breaker is open for ${baseURL}. Request blocked temporarily.`);
    error.code = 'E_CIRCUIT_OPEN';
    return error;
};
const attachResilienceInterceptors = (client, config) => {
    const resilience = config.resilience ?? {};
    const maxRetries = resilience.maxRetries ?? 2;
    const baseRetryDelayMs = resilience.baseRetryDelayMs ?? 250;
    const circuitFailureThreshold = resilience.circuitFailureThreshold ?? 5;
    const circuitOpenMs = resilience.circuitOpenMs ?? 15000;
    const retryOnStatuses = new Set(resilience.retryOnStatuses ?? [408, 425, 429, 500, 502, 503, 504]);
    let circuitState = 'CLOSED';
    let consecutiveFailures = 0;
    let circuitOpenUntil = 0;
    const markSuccess = () => {
        consecutiveFailures = 0;
        circuitState = 'CLOSED';
        circuitOpenUntil = 0;
    };
    const markFailure = () => {
        consecutiveFailures += 1;
        if (consecutiveFailures >= circuitFailureThreshold) {
            circuitState = 'OPEN';
            circuitOpenUntil = Date.now() + circuitOpenMs;
        }
    };
    client.interceptors.request.use((requestConfig) => {
        if (circuitState === 'OPEN') {
            if (Date.now() >= circuitOpenUntil) {
                circuitState = 'HALF_OPEN';
            }
            else {
                return Promise.reject(createCircuitOpenError(String(client.defaults.baseURL ?? 'unknown-base-url')));
            }
        }
        return requestConfig;
    });
    client.interceptors.response.use((response) => {
        markSuccess();
        return response;
    }, async (error) => {
        const requestConfig = (error?.config ?? {});
        const methodValue = requestConfig?.method;
        const method = typeof methodValue === 'string' ? methodValue : 'get';
        const retryCount = Number(requestConfig.__retryCount ?? 0);
        const allowRetry = requestConfig.__allowRetry === true || isRetryableMethod(method);
        if (allowRetry &&
            retryCount < maxRetries &&
            isRetryableError(error, retryOnStatuses)) {
            const backoff = baseRetryDelayMs * Math.pow(2, retryCount);
            await sleep(backoff);
            requestConfig.__retryCount = retryCount + 1;
            return client.request(requestConfig);
        }
        if (shouldCountAsCircuitFailure(error)) {
            markFailure();
        }
        if (circuitState === 'HALF_OPEN') {
            // A failed probe request should immediately reopen the circuit.
            circuitState = 'OPEN';
            circuitOpenUntil = Date.now() + circuitOpenMs;
        }
        throw error;
    });
};
export const createTaskwayApiClients = (config) => {
    const timeout = config.timeoutMs ?? 10000;
    const api = axios.create({
        baseURL: config.apiBaseUrl,
        timeout,
        headers: { 'Content-Type': 'application/json' },
    });
    const authApi = axios.create({
        baseURL: config.authApiBaseUrl,
        timeout,
        headers: { 'Content-Type': 'application/json' },
    });
    attachResilienceInterceptors(api, config);
    attachResilienceInterceptors(authApi, config);
    // Add 401 interceptor if callback provided
    if (typeof config.onUnauthorized === 'function') {
        const handleUnauthorized = (error) => {
            const status = error?.response?.status;
            const requestUrl = String(error?.config?.url ?? '');
            const isAuthAction = /\/auth\/(login|register)$/i.test(requestUrl);
            if (status === 401 && !isAuthAction) {
                config.onUnauthorized?.();
            }
            return Promise.reject(error);
        };
        api.interceptors.response.use((response) => response, handleUnauthorized);
        authApi.interceptors.response.use((response) => response, handleUnauthorized);
    }
    const setAuthToken = (token) => {
        if (token) {
            api.defaults.headers.common.Authorization = `Bearer ${token}`;
            authApi.defaults.headers.common.Authorization = `Bearer ${token}`;
            return;
        }
        delete api.defaults.headers.common.Authorization;
        delete authApi.defaults.headers.common.Authorization;
    };
    return { api, authApi, setAuthToken };
};
export const getTaskwayApiErrorMessage = (error, fallback, unreachableMessage) => {
    const status = error?.response?.status;
    const apiMessage = error?.response?.data?.message;
    if (apiMessage && typeof apiMessage === 'string') {
        return apiMessage;
    }
    if (status === 429) {
        return 'Too many requests right now. Please wait a moment and try again.';
    }
    if (error?.code === 'ECONNABORTED') {
        return 'Request timed out. Please check your connection and try again.';
    }
    if (!error?.response) {
        return unreachableMessage;
    }
    return fallback;
};
